import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import type { HandlebarsBlockType } from '../../types';
import { HandlebarsBlockStartView } from './HandlebarsBlockStartView';

export interface HandlebarsBlockStartOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    handlebarsBlockStart: {
      insertHandlebarsBlockStart: (attrs: {
        blockType: HandlebarsBlockType;
        variable: string;
        blockId: string;
        description?: string | null;
        isValid?: boolean;
        error?: string | null;
      }) => ReturnType;
      updateHandlebarsBlockStart: (
        blockId: string,
        attrs: Partial<{
          variable: string;
          description: string | null;
          isValid: boolean;
          error: string | null;
        }>
      ) => ReturnType;
    };
  }
}

export const HandlebarsBlockStart = Node.create<HandlebarsBlockStartOptions>({
  name: 'handlebarsBlockStart',

  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false, // Prevent orphaning by disabling drag

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addStorage() {
    return {
      // When editing a block, store the blockId of the end tag to reuse
      editingEndBlockId: null as string | null,
    };
  },

  addAttributes() {
    return {
      blockType: {
        default: 'if',
        parseHTML: (element) =>
          (element.getAttribute('data-block-type') as HandlebarsBlockType) || 'if',
        renderHTML: (attributes) => ({
          'data-block-type': attributes.blockType,
        }),
      },
      variable: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-variable') || '',
        renderHTML: (attributes) => ({
          'data-variable': attributes.variable,
        }),
      },
      blockId: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-block-id') || '',
        renderHTML: (attributes) => ({
          'data-block-id': attributes.blockId,
        }),
      },
      description: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-description') || null,
        renderHTML: (attributes) => {
          if (!attributes.description) return {};
          return { 'data-description': attributes.description };
        },
      },
      isValid: {
        default: true,
        parseHTML: (element) => element.getAttribute('data-valid') !== 'false',
        renderHTML: (attributes) => ({
          'data-valid': attributes.isValid ? 'true' : 'false',
        }),
      },
      error: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-error') || null,
        renderHTML: (attributes) => {
          if (!attributes.error) return {};
          return { 'data-error': attributes.error };
        },
      },
      nonEditable: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-non-editable') === 'true',
        renderHTML: (attributes) => {
          if (!attributes.nonEditable) return {};
          return { 'data-non-editable': 'true' };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-handlebars-block-start]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const blockType = HTMLAttributes.blockType || 'if';
    const variable = HTMLAttributes.variable || '';
    // Handle empty variable - don't include trailing space
    const content = variable ? `{{#${blockType} ${variable}}}` : `{{#${blockType}}}`;
    return [
      'span',
      mergeAttributes(
        { 'data-handlebars-block-start': '' },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
      content,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(HandlebarsBlockStartView, {
      className: 'handlebars-block-start-wrapper',
    });
  },

  addCommands() {
    return {
      insertHandlebarsBlockStart:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              blockType: attrs.blockType,
              variable: attrs.variable,
              blockId: attrs.blockId,
              description: attrs.description ?? null,
              isValid: attrs.isValid ?? true,
              error: attrs.error ?? null,
            },
          });
        },
      updateHandlebarsBlockStart:
        (blockId, attrs) =>
        ({ tr, state }) => {
          let updated = false;
          state.doc.descendants((node, pos) => {
            if (
              node.type.name === this.name &&
              node.attrs.blockId === blockId
            ) {
              const newAttrs = { ...node.attrs, ...attrs };
              tr.setNodeMarkup(pos, undefined, newAttrs);
              updated = true;
              return false; // Stop searching
            }
          });
          return updated;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      // Backspace handling will trigger delete confirmation via the view component
      // For now, we prevent default deletion and let the view handle it
      Backspace: () =>
        this.editor.commands.command(({ state }) => {
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          let isBlockStart = false;
          state.doc.nodesBetween(anchor - 1, anchor, (node) => {
            if (node.type.name === this.name) {
              isBlockStart = true;
              return false;
            }
          });

          // Return true to prevent default, the view will handle the delete confirmation
          return isBlockStart;
        }),
    };
  },
});

export default HandlebarsBlockStart;
